import { fromAPI, VisitMeasurementSummary } from "@cur8/rich-entity";
import { APIFetchError, APITypesV1 } from "@cur8/api-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAPIClient } from "render/context/APIContext";
import { useReporting } from "render/hooks/useReporting";
import { queryKey as visitMeasurementsKey } from "hooks/queries/useVisitMeasurementsQuery";

export function useStoreMeasurementMutation({
  patientId,
  visitId,
}: {
  patientId: string;
  visitId: string;
}) {
  const apiClient = useAPIClient();
  const queryClient = useQueryClient();
  const { logError } = useReporting();

  return useMutation({
    mutationFn(measurement: APITypesV1.CreateMeasurementRequest) {
      return apiClient.measurement.storeMeasurement({ patientId, measurement })
        .result;
    },
    // Retry once on a transport failure (dropped connection, no response
    // at all) or a 5xx — never on a 4xx, that's a real validation error
    // the operator needs to fix, not a transient blip. Narrowed to
    // TypeError specifically (the Fetch spec's guarantee for a network
    // failure) rather than "anything that isn't an APIFetchError" —
    // that would've also retried unrelated bugs and, since an aborted
    // fetch rejects with a DOMException rather than an Error, silently
    // missed real aborts too. TypeError naturally excludes both.
    retry(failureCount, error) {
      if (failureCount >= 1) {
        return false;
      }
      if (error instanceof APIFetchError) {
        return error.response.status >= 500;
      }
      return error instanceof TypeError;
    },
    retryDelay: 500,
    onSuccess(dto) {
      const stored = fromAPI.toMeasurement(dto);
      const key = visitMeasurementsKey(patientId, visitId);
      queryClient.setQueryData(
        key,
        (old: VisitMeasurementSummary | undefined) => {
          const others = (old?.measurements ?? []).filter(
            (m) => m.metricName !== stored.metricName
          );
          return { measurements: [...others, stored] };
        }
      );
      queryClient.invalidateQueries({ queryKey: key, refetchType: "none" });
    },
    onError: (error) => logError(error, "Failed to store measurement"),
  });
}
